import React, { useState, useEffect } from 'react';
import { User, Lock, Mail, Phone, MapPin, Save, CheckCircle, AlertCircle } from 'lucide-react';
import Card from '../components/Card';
import Input from '../components/Input';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';

const MyAccount = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile form
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });

  // Cargar datos del usuario cuando estén disponibles
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
      });
    }
  }, [user]);

  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');

  // Password form
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    setProfileError('');
    setProfileSuccess(false);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    setPasswordError('');
    setPasswordSuccess(false);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileError('');

    const result = await updateProfile(profileData);

    if (result.success) {
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } else {
      setProfileError(result.error);
    }

    setProfileLoading(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }

    if (passwordData.new_password.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');

    const result = await changePassword({
      current_password: passwordData.current_password,
      new_password: passwordData.new_password,
    });

    if (result.success) {
      setPasswordSuccess(true);
      setPasswordData({
        current_password: '',
        new_password: '',
        new_password_confirmation: '',
      });
      setTimeout(() => setPasswordSuccess(false), 3000);
    } else {
      setPasswordError(result.error);
    }

    setPasswordLoading(false);
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-24 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-display font-bold text-[#1B263B] mb-2">
            Mi Cuenta
          </h1>
          <p className="text-[#7A756F]">
            Gestiona tu información personal
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-colors ${
              activeTab === 'profile'
                ? 'bg-primary text-white'
                : 'bg-white text-[#7A756F] border border-[#EFEDEA] hover:border-primary'
            }`}
          >
            <User size={18} />
            Perfil
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-colors ${
              activeTab === 'password'
                ? 'bg-primary text-white'
                : 'bg-white text-[#7A756F] border border-[#EFEDEA] hover:border-primary'
            }`}
          >
            <Lock size={18} />
            Contraseña
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <Card>
            <form onSubmit={handleProfileSubmit} className="space-y-5">
              {profileSuccess && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3 text-green-700 animate-fadeIn">
                  <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm">Perfil actualizado correctamente</p>
                </div>
              )}

              {profileError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-700 animate-fadeIn">
                  <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm">{profileError}</p>
                </div>
              )}

              <Input
                label="Nombre completo"
                name="name"
                value={profileData.name}
                onChange={handleProfileChange}
                icon={User}
                required
              />

              <Input
                label="Correo electrónico"
                name="email"
                type="email"
                value={profileData.email}
                onChange={handleProfileChange}
                icon={Mail}
                disabled
              />

              <Input
                label="Teléfono"
                name="phone"
                type="tel"
                value={profileData.phone}
                onChange={handleProfileChange}
                icon={Phone}
                required
              />

              <Input
                label="Dirección principal"
                name="address"
                value={profileData.address}
                onChange={handleProfileChange}
                icon={MapPin}
                placeholder="Calle, número, departamento"
              />

              <Button
                type="submit"
                size="lg"
                fullWidth
                disabled={profileLoading}
                className="gap-2"
              >
                {profileLoading ? (
                  <LoadingSpinner size="sm" color="white" />
                ) : (
                  <>
                    <Save size={18} />
                    Guardar cambios
                  </>
                )}
              </Button>
            </form>
          </Card>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <Card>
            <form onSubmit={handlePasswordSubmit} className="space-y-5">
              {passwordSuccess && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3 text-green-700 animate-fadeIn">
                  <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm">Contraseña cambiada correctamente</p>
                </div>
              )}

              {passwordError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-700 animate-fadeIn">
                  <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm">{passwordError}</p>
                </div>
              )}

              <Input
                label="Contraseña actual"
                name="current_password"
                type="password"
                value={passwordData.current_password}
                onChange={handlePasswordChange}
                icon={Lock}
                required
              />

              <Input
                label="Nueva contraseña"
                name="new_password"
                type="password"
                value={passwordData.new_password}
                onChange={handlePasswordChange}
                icon={Lock}
                required
              />

              <Input
                label="Confirmar nueva contraseña"
                name="new_password_confirmation"
                type="password"
                value={passwordData.new_password_confirmation}
                onChange={handlePasswordChange}
                icon={Lock}
                required
              />

              <p className="text-sm text-[#7A756F]">
                La contraseña debe tener al menos 6 caracteres.
              </p>

              <Button
                type="submit"
                size="lg"
                fullWidth
                disabled={passwordLoading}
                className="gap-2"
              >
                {passwordLoading ? (
                  <LoadingSpinner size="sm" color="white" />
                ) : (
                  <>
                    <Lock size={18} />
                    Cambiar contraseña
                  </>
                )}
              </Button>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MyAccount;
